import React, {useEffect, useState} from "react";
import { connect } from "react-redux";
import { setLoginDetails } from "../../action/set-action-details";
import Logo from "../../assets/img/logo_.png";
import {HOTEL_NAME} from "../../context/connection-string";
import {encryptData} from "../../context/function";
import {toast} from "react-toastify";
import { get, patch } from "../../services/api-client";
import {useNavigate} from "react-router";
import Button from "../../component/button/button";
import InputField from "../../component/form/input-field";
import { LockClosedIcon, KeyIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import Loader from "../../component/loader/loader";

function ResetPassword(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const token = window.location.href.split('/')[4]
    const [reset, setReset] = useState({
        Password: "",
        cPassword: "",
        EmailAddress: ""
    });
    const [passwordErrors, setPasswordErrors] = useState({
        Password: "",
        cPassword: ""
    });

    const ValidateToken = async () => {
        try {
            const res = await get(`login/forget_password/validate_token/${token}`, { retry: false });
            if (res.data.length === 0) {
                toast.error("Invalid or expired reset token. Please request a new password reset link.");
                setTimeout(() => navigate('/'), 2000);
            } else {
                setReset({
                    ...reset,
                    EmailAddress: res.data[0].email_address
                })
            }
        } catch (err) {
            toast.error("Unable to validate reset token. Please request a new password reset link.");
            setTimeout(() => navigate('/'), 2000);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        ValidateToken();
    }, [])

    const onEdit = (e) => {
        const fieldName = e.target.name;
        const value = e.target.value;
        
        setReset({
            ...reset,
            [fieldName]: value,
        });

        // Clear error when user starts typing
        if (passwordErrors[fieldName]) {
            setPasswordErrors({
                ...passwordErrors,
                [fieldName]: ""
            });
        }

        // Validate password match in real-time
        if (fieldName === 'cPassword' && reset.Password) {
            if (value !== reset.Password) {
                setPasswordErrors({
                    ...passwordErrors,
                    cPassword: "Passwords do not match"
                });
            } else {
                setPasswordErrors({
                    ...passwordErrors,
                    cPassword: ""
                });
            }
        }
    }

    const validatePassword = (password) => {
        if (password.length < 8) {
            return "Password must be at least 8 characters long";
        }
        if (!/(?=.*[a-z])/.test(password)) {
            return "Password must contain at least one lowercase letter";
        }
        if (!/(?=.*[A-Z])/.test(password)) {
            return "Password must contain at least one uppercase letter";
        }
        if (!/(?=.*\d)/.test(password)) {
            return "Password must contain at least one number";
        }
        return "";
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        
        // Validate password
        const passwordError = validatePassword(reset.Password);
        if (passwordError) {
            setPasswordErrors({
                ...passwordErrors,
                Password: passwordError
            });
            toast.error(passwordError);
            return;
        }

        // Validate password match
        if (reset.Password !== reset.cPassword) {
            setPasswordErrors({
                ...passwordErrors,
                cPassword: "Passwords do not match"
            });
            toast.error('Passwords do not match');
            return;
        }

        setIsSubmitting(true);
        const formData = {
            Password: encryptData(reset.Password),
            EmailAddress: reset.EmailAddress
        }
        
        try {
            const result = await patch(`login/forget_password/change_password/${token}`, formData, { retry: false });
            if (result.data.message === "success") {
                toast.success('Your password has been changed successfully.');
                setTimeout(() => navigate('/'), 1500);
            } else {
                toast.error('Failed to reset password. Please try again.')
            }
        } catch (e) {
            // Error message already shown by API client
            console.log('NETWORK ERROR', e)
        } finally {
            setIsSubmitting(false);
        }
    }
    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 px-4 sm:px-6 lg:px-8">
            {/* Background Pattern */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 dark:bg-purple-900 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-xl opacity-70 animate-blob"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-pink-300 dark:bg-pink-900 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-300 dark:bg-indigo-900 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
            </div>

            {/* Reset Password Card */}
            <div className="relative w-full max-w-md">
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 sm:p-10 backdrop-blur-sm border border-white/20 dark:border-slate-700/50">
                    {/* Logo and Header */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-20 h-20 mb-4 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
                            <KeyIcon className="w-10 h-10 text-white" />
                        </div>
                        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                            Reset Password
                        </h1>
                        <p className="text-slate-600 dark:text-slate-400">
                            Enter your new password below
                        </p>
                    </div>

                    {/* Password Requirements Info */}
                    <div className="mb-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                        <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-2">
                            Password Requirements:
                        </p>
                        <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
                            <li>At least 8 characters long</li>
                            <li>Contains uppercase and lowercase letters</li>
                            <li>Contains at least one number</li>
                        </ul>
                    </div>

                    {/* Form */}
                    <form onSubmit={onSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="Password" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                New Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <LockClosedIcon className="h-5 w-5 text-slate-400" />
                                </div>
                                <input
                                    type="password"
                                    id="Password"
                                    name="Password"
                                    value={reset.Password}
                                    onChange={onEdit}
                                    placeholder="Enter your new password"
                                    required
                                    autoComplete="new-password"
                                    className={`w-full pl-10 pr-4 py-2.5 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                                        passwordErrors.Password
                                            ? "border-red-500 focus:ring-red-500 focus:border-red-500 dark:border-red-500"
                                            : "border-slate-300 dark:border-slate-600 focus:ring-indigo-500 focus:border-transparent"
                                    }`}
                                />
                            </div>
                            {passwordErrors.Password && (
                                <p className="mt-1.5 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                                    <CheckCircleIcon className="h-4 w-4" />
                                    {passwordErrors.Password}
                                </p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="cPassword" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                Confirm Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <LockClosedIcon className="h-5 w-5 text-slate-400" />
                                </div>
                                <input
                                    type="password"
                                    id="cPassword"
                                    name="cPassword"
                                    value={reset.cPassword}
                                    onChange={onEdit}
                                    placeholder="Confirm your new password"
                                    required
                                    autoComplete="new-password"
                                    className={`w-full pl-10 pr-4 py-2.5 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                                        passwordErrors.cPassword
                                            ? "border-red-500 focus:ring-red-500 focus:border-red-500 dark:border-red-500"
                                            : "border-slate-300 dark:border-slate-600 focus:ring-indigo-500 focus:border-transparent"
                                    }`}
                                />
                            </div>
                            {passwordErrors.cPassword && (
                                <p className="mt-1.5 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                                    <CheckCircleIcon className="h-4 w-4" />
                                    {passwordErrors.cPassword}
                                </p>
                            )}
                            {reset.cPassword && reset.Password === reset.cPassword && !passwordErrors.cPassword && (
                                <p className="mt-1.5 text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                    <CheckCircleIcon className="h-4 w-4" />
                                    Passwords match
                                </p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            size="lg"
                            fullWidth
                            isLoading={isSubmitting}
                            disabled={isSubmitting}
                            className="mt-6"
                        >
                            {isSubmitting ? "Resetting Password..." : "Reset Password"}
                        </Button>
                    </form>

                    {/* Footer */}
                    <div className="mt-8 text-center">
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            &copy; {new Date().getFullYear()} {HOTEL_NAME}. All rights reserved.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = (state) => {
    return {
        loginData: state.LoginDetails,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setOnLoginDetails: (p) => {
            dispatch(setLoginDetails(p));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);